// client/src/components/notes/NotesPanel.jsx
//
// The Notes section on an event / event item / event action / post /
// campaign page: the notes associated with that record, plus "Add note"
// (pre-linked to the record) and click-to-edit.
//
// Event-action pages are routed by event + item, so for refType
// 'event_action' pass `itemId` and the panel looks up the action id.

import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FaPlus, FaStickyNote } from 'react-icons/fa';
import IconButton from '../common/IconButton';
import { listNotes, getActionIdForItem } from '../../services/notesApi';
import NoteEditorModal from './NoteEditorModal';
import NoteCard from './NoteCard';
import { typeLabel } from './noteTypes';
import './Notes.css';

export default function NotesPanel({ tenantId, refType, refId: refIdProp, itemId, label, className = '' }) {
  const { t } = useTranslation();
  const [refId, setRefId] = useState(refIdProp || null);
  const [resolving, setResolving] = useState(refType === 'event_action' && !refIdProp);
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState(null); // null | 'new' | note

  useEffect(() => {
    if (refIdProp) { setRefId(refIdProp); setResolving(false); return undefined; }
    if (refType !== 'event_action' || !itemId) { setResolving(false); return undefined; }
    let alive = true;
    setResolving(true);
    getActionIdForItem(tenantId, itemId)
      .then((res) => { if (alive) setRefId(res.data?.actionId || null); })
      .catch((err) => { if (alive) setError(err.message); })
      .finally(() => { if (alive) setResolving(false); });
    return () => { alive = false; };
  }, [tenantId, refType, refIdProp, itemId]);

  const load = useCallback(async () => {
    if (!refId) { setLoading(false); return; }
    setLoading(true);
    setError('');
    try {
      const res = await listNotes(tenantId, { refType, refId });
      setNotes(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setError(err.message || t('notes.loadFailed', 'Could not load notes'));
    } finally {
      setLoading(false);
    }
  }, [tenantId, refType, refId, t]);

  useEffect(() => { if (!resolving) load(); }, [load, resolving]);

  // An item with no action yet has nothing to attach action notes to.
  if (!resolving && !refId && !error) return null;

  const selfLink = refId ? { type: refType, id: Number(refId), label: label || typeLabel(t, refType) } : null;
  const isSelf = (l) => l.type === refType && Number(l.id) === Number(refId);

  return (
    <section className={`surface notes-panel ${className}`.trim()} aria-labelledby={`notes-panel-${refType}`}>
      <div className="notes-panel__head">
        <h4 id={`notes-panel-${refType}`}>
          <FaStickyNote aria-hidden="true" /> {t('notes.title', 'Notes')}
          {!loading && notes.length > 0 && <span className="notes-panel__count">{notes.length}</span>}
        </h4>
        <IconButton label={t('notes.addNote', 'Add note')} onClick={() => setEditing('new')} disabled={!refId}>
          <FaPlus aria-hidden="true" />
        </IconButton>
      </div>

      {error && <div className="note error">{error}</div>}
      {!error && (loading || resolving) && <div className="surface-muted">{t('common.loading', 'Loading…')}</div>}
      {!error && !loading && !resolving && notes.length === 0 && (
        <div className="surface-muted">{t('notes.panelEmpty', 'No notes yet.')}</div>
      )}

      {!loading && notes.length > 0 && (
        <ul className="notes-panel__list">
          {notes.map((n) => (
            <li key={n.id}>
              <NoteCard
                note={n}
                tenantId={tenantId}
                hideLink={isSelf}
                onOpen={() => setEditing(n)}
                compact
              />
            </li>
          ))}
        </ul>
      )}

      {editing && (
        <NoteEditorModal
          tenantId={tenantId}
          note={editing === 'new' ? null : editing}
          initialLinks={selfLink ? [selfLink] : []}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
          onDeleted={() => { setEditing(null); load(); }}
        />
      )}
    </section>
  );
}
